<template>
  <div class="inputGroup">
    <ConfirmModal
      v-if="showEnableConfirmModal"
      key="enable-secure-field-modal"
      title="Enable Secured Files"
      confirm-text="Secure Files"
      :z-index="2"
      show-learn-more
      :learn-more-url="learnMoreUrl"
      @close="cancelConfirm()"
      @confirm="applyConfirm()"
    >
      <p class="mb-3">
        Turning this on applies stricter access checks to downloads for this
        field, including stricter enforcement for HIPAA accounts.
      </p>

      <p class="mb-0">
        This does not change how your files are stored (all files are always
        stored in a private, secure AWS bucket), and the file links the app
        generates continue to work until they expire on their own (within 7 days).
      </p>
    </ConfirmModal>

    <ConfirmModal
      v-else-if="showDisableConfirmModal"
      key="disable-secure-field-modal"
      title="Disable Secured Files"
      confirm-text="Disable Secured Files"
      confirm-type="alert"
      :z-index="2"
      show-learn-more
      :learn-more-url="learnMoreUrl"
      @close="cancelConfirm()"
      @confirm="applyConfirm()"
    >
      <p class="mb-3">
        Turning this off removes the extra access checks Secured Files applies to
        downloads, including stricter enforcement for HIPAA accounts.
      </p>

      <p class="mb-0">
        This does not change how your files are stored (all files are always
        stored in a private, secure AWS bucket), and the file links the app
        generates continue to work until they expire on their own (within 7 days).
      </p>
    </ConfirmModal>

    <div>
      <div class="flex items-center justify-between mb-2">
        <label class="text-default text-sm font-medium leading-4">Secured Files</label>
        <a
          :href="learnMoreUrl"
          class="underline link-with-icon link-with-icon--right text-default"
          target="_blank"
        >Learn more
          <Icon
            type="open-in-new"
            class="link-with-icon__icon ml-2"
          />
        </a>
      </div>
      <div>
        <select
          v-model="fieldLocal.format.secure"
          class="text-base py-2 pl-3 leading-5"
        >
          <option :value="false">
            No
          </option>
          <option :value="true">
            Yes
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script>
import ConfirmModal from '@/components/ui/ConfirmModal';
import Icon from '@/components/ui/Icon';

export default {
  name: 'File',
  components: {
    ConfirmModal,
    Icon,
  },
  props: {
    fieldLocal: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      // The field's saved value when the settings opened. Moving away from it on an
      // existing field triggers a confirmation; returning to it is a no-op and must
      // not prompt (the modal copy describes consequences for the saved state).
      savedSecure: this.fieldLocal.format.secure,
      hasConfirmedChange: false,
    };
  },
  computed: {
    learnMoreUrl() {
      return 'https://docs.knack.com/docs/file-fields';
    },
    isExistingField() {
      // New fields carry the placeholder key 'new' until saved (Field.fieldDefault);
      // toggling them needs no confirmation because no download links exist yet
      return Boolean(this.fieldLocal.key) && this.fieldLocal.key !== 'new';
    },
    confirmAction() {
      if (!this.isExistingField || this.hasConfirmedChange) {
        return null;
      }

      if (this.fieldLocal.format.secure === this.savedSecure) {
        return null;
      }

      return this.fieldLocal.format.secure ? 'enable' : 'disable';
    },
    showEnableConfirmModal() {
      return this.confirmAction === 'enable';
    },
    showDisableConfirmModal() {
      return this.confirmAction === 'disable';
    },
  },
  watch: {
    'fieldLocal.format.secure': {
      handler(newValue) {
        if (newValue === this.savedSecure) {
          this.hasConfirmedChange = false;
        }
      },
    },
  },
  methods: {
    applyConfirm() {
      this.hasConfirmedChange = true;
    },
    cancelConfirm() {
      this.fieldLocal.format.secure = this.savedSecure;
    },
  },
};
</script>
